import type { Size } from "odyssey-shared";

/**
 * The world map's view geometry (ADR-100 decision 5, the amendment's decisions 26 and 27): one zoom of 2 logical pixels
 * a tile, a view that follows the character until dragged, and the arrow at the view's edge that points to a place
 * outside it. Everything here is pure: positions are tiles, the picture and the view are in logical pixels, and the
 * origin of a view is the picture pixel at its top left corner, always whole.
 */

/**
 * Logical pixels a tile covers on the map (decision 5), and so the scale of the picture's canvas.
 */
export const MAP_SCALE = 2;

/**
 * How far in from the view's edge an arrow's centre lies, the card's 10 px.
 */
export const EDGE_INSET = 10;

/**
 * The picture pixel at a view's top left corner.
 */
export interface Origin {
  readonly left: number;
  readonly top: number;
}

/**
 * A point in tiles.
 */
export interface TilePoint {
  readonly x: number;
  readonly y: number;
}

/**
 * An arrow at the edge of a view: the centre of its plaque in view pixels and the angle it points, in radians, 0 to the
 * right and positive downward.
 */
export interface EdgeMark {
  readonly x: number;
  readonly y: number;
  readonly angle: number;
}

/**
 * Where a view's origin lies along one axis: inside the picture, or centred when the picture is smaller than the view.
 *
 * @param origin The wanted origin.
 * @param view The view's size along the axis.
 * @param picture The picture's size along the axis.
 * @returns The origin kept inside the picture.
 */
function clampAxis(origin: number, view: number, picture: number): number {
  if (picture <= view) {
    return -Math.floor((view - picture) / 2);
  }
  return Math.max(0, Math.min(picture - view, origin));
}

/**
 * Keeps a view's origin inside the picture; a picture smaller than the view is centred in it.
 *
 * @param origin The wanted origin.
 * @param view The view's size in logical pixels.
 * @param picture The picture's size in logical pixels.
 * @returns The origin, whole numbers.
 */
export function clampOrigin(origin: Origin, view: Size, picture: Size): Origin {
  return { left: clampAxis(Math.round(origin.left), view.width, picture.width), top: clampAxis(Math.round(origin.top), view.height, picture.height) };
}

/**
 * The origin that centres a tile on the view, kept inside the picture.
 *
 * @param centre The tile to centre on, in tiles.
 * @param view The view's size in logical pixels.
 * @param picture The picture's size in logical pixels.
 * @returns The origin.
 */
export function viewOrigin(centre: TilePoint, view: Size, picture: Size): Origin {
  return clampOrigin({ left: centre.x * MAP_SCALE - view.width / 2, top: centre.y * MAP_SCALE - view.height / 2 }, view, picture);
}

/**
 * Moves a view with a drag: the picture follows the pointer, so the origin moves against it.
 *
 * @param origin The view's origin now.
 * @param dx The pointer's move across in logical pixels.
 * @param dy The pointer's move down in logical pixels.
 * @param view The view's size.
 * @param picture The picture's size.
 * @returns The new origin, kept inside the picture.
 */
export function panOrigin(origin: Origin, dx: number, dy: number, view: Size, picture: Size): Origin {
  return clampOrigin({ left: origin.left - dx, top: origin.top - dy }, view, picture);
}

/**
 * The tile under a point of the view.
 *
 * @param origin The view's origin.
 * @param x The point across, in view pixels.
 * @param y The point down, in view pixels.
 * @returns The point in tiles, fractional.
 */
export function tilePoint(origin: Origin, x: number, y: number): TilePoint {
  return { x: (origin.left + x) / MAP_SCALE, y: (origin.top + y) / MAP_SCALE };
}

/**
 * Where a tile lies in the view.
 *
 * @param origin The view's origin.
 * @param tile The point in tiles.
 * @returns The point in view pixels, fractional.
 */
export function pixelPoint(origin: Origin, tile: TilePoint): { readonly x: number; readonly y: number } {
  return { x: tile.x * MAP_SCALE - origin.left, y: tile.y * MAP_SCALE - origin.top };
}

/**
 * The arrow that points to a place outside the view, on the line from the view's centre to the place, where it meets the
 * rectangle `inset` pixels inside the view's edge.
 *
 * @param target The place in tiles.
 * @param origin The view's origin.
 * @param view The view's size.
 * @param inset How far in the arrow's centre lies; the card's 10 px unless given.
 * @returns The arrow, or null when the place lies inside the view.
 */
export function edgeMark(target: TilePoint, origin: Origin, view: Size, inset: number = EDGE_INSET): EdgeMark | null {
  const point = pixelPoint(origin, target);
  if (point.x >= 0 && point.y >= 0 && point.x < view.width && point.y < view.height) {
    return null;
  }
  const centreX = view.width / 2;
  const centreY = view.height / 2;
  const dx = point.x - centreX;
  const dy = point.y - centreY;
  const reachX = dx === 0 ? Infinity : (dx > 0 ? view.width - inset - centreX : centreX - inset) / Math.abs(dx);
  const reachY = dy === 0 ? Infinity : (dy > 0 ? view.height - inset - centreY : centreY - inset) / Math.abs(dy);
  const along = Math.min(reachX, reachY, 1);
  return { x: Math.round(centreX + dx * along), y: Math.round(centreY + dy * along), angle: Math.atan2(dy, dx) };
}
